import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Chart.js 등록
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// 한국 소아 평균 데이터 (0-72개월, 질병관리청 2017 성장도표)
const koreanAverageData = {
  boy: {
    0: { height: 49.9, weight: 3.3 },
    1: { height: 54.7, weight: 4.5 },
    2: { height: 58.4, weight: 5.6 },
    3: { height: 61.4, weight: 6.4 },
    4: { height: 63.9, weight: 7.0 },
    5: { height: 65.9, weight: 7.5 },
    6: { height: 67.6, weight: 7.9 },
    7: { height: 69.2, weight: 8.3 },
    8: { height: 70.6, weight: 8.6 },
    9: { height: 72.0, weight: 8.9 },
    10: { height: 73.3, weight: 9.2 },
    11: { height: 74.5, weight: 9.4 },
    12: { height: 75.7, weight: 9.6 },
    13: { height: 76.9, weight: 9.9 },
    14: { height: 78.0, weight: 10.1 },
    15: { height: 79.1, weight: 10.3 },
    16: { height: 80.2, weight: 10.5 },
    17: { height: 81.2, weight: 10.7 },
    18: { height: 82.3, weight: 10.9 },
    19: { height: 83.2, weight: 11.1 },
    20: { height: 84.2, weight: 11.3 },
    21: { height: 85.1, weight: 11.5 },
    22: { height: 86.0, weight: 11.8 },
    23: { height: 86.9, weight: 12.0 },
    24: { height: 87.8, weight: 12.2 },
    30: { height: 90.7, weight: 12.7 },
    36: { height: 95.7, weight: 14.2 },
    42: { height: 101.9, weight: 16.3 },
    48: { height: 105.1, weight: 17.3 },
    54: { height: 108.4, weight: 18.4 },
    60: { height: 114.7, weight: 20.7 },
    66: { height: 117.8, weight: 22.0 },
    72: { height: 120.8, weight: 23.4 }
  },
  girl: {
    0: { height: 49.1, weight: 3.2 },
    1: { height: 53.7, weight: 4.2 },
    2: { height: 57.1, weight: 5.1 },
    3: { height: 59.8, weight: 5.8 },
    4: { height: 62.1, weight: 6.4 },
    5: { height: 64.0, weight: 6.9 },
    6: { height: 65.7, weight: 7.3 },
    7: { height: 67.3, weight: 7.6 },
    8: { height: 68.7, weight: 7.9 },
    9: { height: 70.1, weight: 8.2 },
    10: { height: 71.5, weight: 8.5 },
    11: { height: 72.8, weight: 8.7 },
    12: { height: 74.0, weight: 8.9 },
    13: { height: 75.2, weight: 9.2 },
    14: { height: 76.4, weight: 9.4 },
    15: { height: 77.5, weight: 9.6 },
    16: { height: 78.6, weight: 9.8 },
    17: { height: 79.7, weight: 10.0 },
    18: { height: 80.7, weight: 10.2 },
    19: { height: 81.7, weight: 10.4 },
    20: { height: 82.7, weight: 10.6 },
    21: { height: 83.7, weight: 10.9 },
    22: { height: 84.6, weight: 11.1 },
    23: { height: 85.5, weight: 11.3 },
    24: { height: 87.1, weight: 12.2 },
    30: { height: 91.9, weight: 13.3 },
    36: { height: 96.5, weight: 14.7 },
    42: { height: 103.1, weight: 16.8 },
    48: { height: 106.3, weight: 17.9 },
    54: { height: 112.8, weight: 20.1 },
    60: { height: 119.0, weight: 22.7 },
    66: { height: 122.1, weight: 24.2 },
    72: { height: 127.9, weight: 27.5 }
  }
};

// 중간 개월 수 데이터를 선형 보간으로 계산
const getInterpolatedData = (data, month) => {
  if (data[month]) return data[month];

  // 가장 가까운 두 데이터 포인트 찾기
  const months = Object.keys(data).map(Number).sort((a, b) => a - b);
  let lowerMonth = 0;
  let upperMonth = months[months.length - 1];

  for (let i = 0; i < months.length - 1; i++) {
    if (months[i] <= month && month <= months[i + 1]) {
      lowerMonth = months[i];
      upperMonth = months[i + 1];
      break;
    }
  }

  if (month > upperMonth) return data[upperMonth]; // 최대값 초과시 마지막 데이터 반환

  // 선형 보간
  const ratio = (month - lowerMonth) / (upperMonth - lowerMonth);
  const lowerData = data[lowerMonth];
  const upperData = data[upperMonth];

  return {
    height: parseFloat((lowerData.height + (upperData.height - lowerData.height) * ratio).toFixed(1)),
    weight: parseFloat((lowerData.weight + (upperData.weight - lowerData.weight) * ratio).toFixed(1))
  };
};

function GrowthChart({ userId, profile, onClose, guestMode }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState('height'); // 'height' or 'weight'
  const [viewMode, setViewMode] = useState('absolute'); // 'absolute' or 'growth'
  const [showAverage, setShowAverage] = useState(true); // 평균선 표시 여부

  useEffect(() => {
    loadRecords();
  }, [userId]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      if (guestMode) {
        // 비회원 모드: localStorage에서 데이터 불러오기
        const guestRecords = JSON.parse(localStorage.getItem('guestRecords') || '[]');
        // 날짜 기준으로 정렬 (오래된 순)
        guestRecords.sort((a, b) => new Date(a.date) - new Date(b.date));
        setRecords(guestRecords);
      } else {
        // 로그인 사용자: Firestore에서 데이터 불러오기
        const recordsRef = collection(db, 'users', userId, 'records');
        const q = query(recordsRef, orderBy('date', 'asc'));
        const querySnapshot = await getDocs(q);

        const recordsData = [];
        querySnapshot.forEach((doc) => {
          recordsData.push(doc.data());
        });

        setRecords(recordsData);
      }
    } catch (error) {
      console.error('기록 로드 실패:', error);
      alert('기록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 각 기록 날짜에 대한 월령 계산
  const calculateAgeInMonths = (birthDate, recordDate) => {
    const birth = new Date(birthDate);
    const record = new Date(recordDate);
    return Math.floor((record - birth) / (1000 * 60 * 60 * 24 * 30.44));
  };

  // 날짜 형식 변환 (2025-10-24 → 25.10.24)
  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return `${year.slice(2)}.${month}.${day}`;
  };

  // 평균 데이터 생성
  const generateAverageData = (type) => {
    if (!profile || !showAverage || viewMode !== 'absolute') return null;

    return records.map(record => {
      const ageInMonths = Math.min(calculateAgeInMonths(profile.birthDate, record.date), 72);
      const avgData = getInterpolatedData(koreanAverageData[profile.gender], ageInMonths);
      return avgData ? avgData[type] : null;
    });
  };

  const heightDatasets = [
    {
      label: '우리 아기 키 (cm)',
      data: records.map(r => r.height),
      borderColor: '#E8A87C',
      backgroundColor: 'rgba(232, 168, 124, 0.1)',
      tension: 0.4,
      fill: true,
      pointRadius: 5,
      pointHoverRadius: 7,
      pointBackgroundColor: '#E8A87C',
      pointBorderColor: '#fff',
      pointBorderWidth: 2
    }
  ];

  const averageHeightData = generateAverageData('height');
  if (averageHeightData && showAverage && viewMode === 'absolute') {
    heightDatasets.push({
      label: '평균 키 (cm)',
      data: averageHeightData,
      borderColor: '#9E9E9E',
      backgroundColor: 'transparent',
      borderDash: [5, 5],
      tension: 0.4,
      fill: false,
      pointRadius: 3,
      pointHoverRadius: 5,
      pointBackgroundColor: '#9E9E9E',
      pointBorderColor: '#fff',
      pointBorderWidth: 1
    });
  }

  const heightData = {
    labels: records.map(r => formatDate(r.date)),
    datasets: heightDatasets
  };

  const weightDatasets = [
    {
      label: '우리 아기 몸무게 (kg)',
      data: records.map(r => r.weight),
      borderColor: '#D4956B',
      backgroundColor: 'rgba(212, 149, 107, 0.1)',
      tension: 0.4,
      fill: true,
      pointRadius: 5,
      pointHoverRadius: 7,
      pointBackgroundColor: '#D4956B',
      pointBorderColor: '#fff',
      pointBorderWidth: 2
    }
  ];

  const averageWeightData = generateAverageData('weight');
  if (averageWeightData && showAverage && viewMode === 'absolute') {
    weightDatasets.push({
      label: '평균 몸무게 (kg)',
      data: averageWeightData,
      borderColor: '#9E9E9E',
      backgroundColor: 'transparent',
      borderDash: [5, 5],
      tension: 0.4,
      fill: false,
      pointRadius: 3,
      pointHoverRadius: 5,
      pointBackgroundColor: '#9E9E9E',
      pointBorderColor: '#fff',
      pointBorderWidth: 1
    });
  }

  const weightData = {
    labels: records.map(r => formatDate(r.date)),
    datasets: weightDatasets
  };

  // 증가량 데이터 계산
  const getGrowthData = () => {
    if (records.length < 2) return { heightGrowth: [], weightGrowth: [], labels: [] };

    const heightGrowth = [];
    const weightGrowth = [];
    const labels = [];

    for (let i = 1; i < records.length; i++) {
      heightGrowth.push((records[i].height - records[i-1].height).toFixed(1));
      weightGrowth.push((records[i].weight - records[i-1].weight).toFixed(2));
      labels.push(`${formatDate(records[i-1].date)} → ${formatDate(records[i].date)}`);
    }

    return { heightGrowth, weightGrowth, labels };
  };

  const growthData = getGrowthData();

  const heightGrowthData = {
    labels: growthData.labels,
    datasets: [
      {
        label: '키 증가량 (cm)',
        data: growthData.heightGrowth,
        backgroundColor: '#A8D5BA',
        borderColor: '#66BB6A',
        borderWidth: 2
      }
    ]
  };

  const weightGrowthData = {
    labels: growthData.labels,
    datasets: [
      {
        label: '몸무게 증가량 (kg)',
        data: growthData.weightGrowth,
        backgroundColor: '#FFD54F',
        borderColor: '#FFB74D',
        borderWidth: 2
      }
    ]
  };

  // Y축 범위 자동 계산
  const getYAxisRange = (type) => {
    if (viewMode !== 'absolute' || records.length === 0) return {};

    const values = records.map(r => type === 'height' ? r.height : r.weight);
    const averageValues = generateAverageData(type) || [];
    const allValues = [...values, ...averageValues.filter(v => v !== null)];

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const padding = (maxValue - minValue) * 0.15; // 15% 여유 공간

    return {
      min: Math.floor(minValue - padding),
      max: Math.ceil(maxValue + padding)
    };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1.5,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#8B7355',
          font: {
            size: 14,
            weight: 'bold'
          },
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#8B7355',
        bodyColor: '#8B7355',
        borderColor: '#E8A87C',
        borderWidth: 2,
        padding: 12,
        displayColors: false
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        ...getYAxisRange(chartType),
        ticks: {
          color: '#8B7355',
          font: {
            size: 12
          }
        },
        grid: {
          color: 'rgba(139, 115, 85, 0.1)'
        }
      },
      x: {
        ticks: {
          color: '#8B7355',
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 11
          }
        },
        grid: {
          color: 'rgba(139, 115, 85, 0.1)'
        }
      }
    }
  };

  return (
    <div className="fade-in" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div className="slide-up" style={{
        backgroundColor: 'white',
        borderRadius: '24px',
        padding: '40px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '80vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#8B7355',
            zIndex: 10
          }}
        >
          ×
        </button>

        <div style={{
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <h2 style={{
            color: '#E8A87C',
            fontSize: '24px',
            marginBottom: '8px'
          }}>
            성장 그래프
          </h2>
          <p style={{
            color: '#8B7355',
            fontSize: '14px'
          }}>
            시간에 따른 성장 변화
          </p>
        </div>

        {loading ? (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <div className="skeleton skeleton-card"></div>
              <div className="skeleton skeleton-card" style={{ height: '48px' }}></div>
            </div>
            <div className="skeleton skeleton-card" style={{ height: '250px' }}></div>
          </div>
        ) : records.length < 2 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#8B7355'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
            <p>그래프를 표시하려면 최소 2개 이상의 기록이 필요합니다.</p>
            <p style={{ fontSize: '14px', marginTop: '8px' }}>
              "기록 추가" 버튼을 눌러 더 많은 기록을 남겨보세요!
            </p>
          </div>
        ) : (
          <>
            {/* 보기 모드 선택 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: viewMode === 'absolute' ? '1fr 1fr 1fr' : '1fr 1fr',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <button
                onClick={() => setViewMode('absolute')}
                style={{
                  padding: '10px',
                  backgroundColor: viewMode === 'absolute' ? '#66BB6A' : 'white',
                  color: viewMode === 'absolute' ? 'white' : '#8B7355',
                  border: `2px solid ${viewMode === 'absolute' ? '#66BB6A' : '#F5E6D3'}`,
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                📈 절대값
              </button>
              <button
                onClick={() => setViewMode('growth')}
                style={{
                  padding: '10px',
                  backgroundColor: viewMode === 'growth' ? '#FFB74D' : 'white',
                  color: viewMode === 'growth' ? 'white' : '#8B7355',
                  border: `2px solid ${viewMode === 'growth' ? '#FFB74D' : '#F5E6D3'}`,
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                📊 증가량
              </button>
              {viewMode === 'absolute' && (
                <button
                  onClick={() => setShowAverage(!showAverage)}
                  style={{
                    padding: '10px',
                    backgroundColor: showAverage ? '#9E9E9E' : 'white',
                    color: showAverage ? 'white' : '#8B7355',
                    border: `2px solid ${showAverage ? '#9E9E9E' : '#F5E6D3'}`,
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {showAverage ? '✓ 평균선' : '평균선'}
                </button>
              )}
            </div>

            {/* 차트 종류 선택 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <button
                onClick={() => setChartType('height')}
                style={{
                  padding: '12px',
                  backgroundColor: chartType === 'height' ? '#E8A87C' : 'white',
                  color: chartType === 'height' ? 'white' : '#8B7355',
                  border: `2px solid ${chartType === 'height' ? '#E8A87C' : '#F5E6D3'}`,
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                📏 키
              </button>
              <button
                onClick={() => setChartType('weight')}
                style={{
                  padding: '12px',
                  backgroundColor: chartType === 'weight' ? '#D4956B' : 'white',
                  color: chartType === 'weight' ? 'white' : '#8B7355',
                  border: `2px solid ${chartType === 'weight' ? '#D4956B' : '#F5E6D3'}`,
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                ⚖️ 몸무게
              </button>
            </div>

            <div style={{
              backgroundColor: '#FAF3E8',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '20px',
              height: '400px'
            }}>
              {viewMode === 'absolute' ? (
                <Line
                  data={chartType === 'height' ? heightData : weightData}
                  options={options}
                />
              ) : (
                <Bar
                  data={chartType === 'height' ? heightGrowthData : weightGrowthData}
                  options={options}
                />
              )}
            </div>

            <div style={{
              backgroundColor: '#F5E6D3',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
              color: '#8B7355',
              fontSize: '14px'
            }}>
              <strong>총 {records.length}개</strong>의 기록으로 그래프를 표시하고 있습니다
            </div>
          </>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#E8A87C',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginTop: '20px',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#D4956B'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#E8A87C'}
        >
          닫기
        </button>
      </div>
    </div>
  );
}

export default GrowthChart;
